import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import AnimatedChatDemo from './component/AnimatedChatDemo';
import HeroCitationConsole from './component/HeroCitationConsole';
import './component/ChatDemo.css';

const IconLightning = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M11 2L4 11h6l-1 7 7-9h-6l1-7z" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDocument = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z" stroke="#3b82f6" strokeWidth="1.5" />
    <path d="M8 7h4M8 10h4M8 13h2" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconLock = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="4" y="9" width="12" height="9" rx="2" stroke="#3b82f6" strokeWidth="1.5" />
    <path d="M7 9V6a3 3 0 016 0v3" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconSearch = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="5" stroke="#3b82f6" strokeWidth="1.5" />
    <path d="M16 16l-3-3" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconBuilding = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M3 18V8l7-5 7 5v10" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M8 18v-5h4v5" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const features = [
  {
    icon: <IconLightning />,
    title: 'Answers in seconds, not days',
    desc: 'Ask any Oracle HCM question and get a precise, step-by-step answer immediately.',
  },
  {
    icon: <IconDocument />,
    title: 'Always current with Oracle releases',
    desc: 'Knows what changed in each quarterly release and what your team needs to action.',
  },
  {
    icon: <IconLock />,
    title: 'Your data never leaves your environment',
    desc: 'Every company gets a completely isolated knowledge base. Zero data mixing.',
  },
  {
    icon: <IconSearch />,
    title: 'Grounded in official Oracle documentation',
    desc: 'Every answer cites real Oracle documentation — not hallucinated guidance.',
  },
  {
    icon: <IconBuilding />,
    title: 'Knows your specific implementation',
    desc: 'Upload your configuration docs and get answers specific to your exact setup.',
  },
];

const securityItems = [
  {
    title: 'Data isolation by architecture',
    desc: 'Every tenant runs in a completely separate vector database collection. Isolation is structural, not policy-based.',
  },
  {
    title: 'Canadian data residency',
    desc: 'All data stored and processed on Canadian servers. Your Oracle HCM data never crosses the border.',
  },
  {
    title: 'Encrypted end-to-end',
    desc: 'TLS 1.3 in transit. Encrypted at rest. No exceptions.',
  },
  {
    title: 'Zero data retention by the AI',
    desc: "We use Anthropic's API under their zero data retention policy. Your queries are never stored or used for training.",
  },
  {
    title: 'Token-based authentication',
    desc: 'JWT with 24-hour expiry and server-side blacklisting on logout. No persistent sessions.',
  },
  {
    title: 'Full audit trail',
    desc: 'Every action logged — queries, logins, uploads — with user, tenant, and timestamp context.',
  },
];

const DemoWindowBar = ({ url = 'Edge AI — Oracle HCM Expert' }) => (
  <div className="demo-window-bar">
    <div className="demo-dots">
      <span className="demo-dot red" />
      <span className="demo-dot yellow" />
      <span className="demo-dot green" />
    </div>
    <span className="demo-url">{url}</span>
  </div>
);

const LandingPage = () => {
  useEffect(() => {
    const container = document.querySelector('.landing-page');
    if (!container) return;

    let lastScrollY = container.scrollTop;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = container.scrollTop;
          const announcement = document.querySelector('.announcement-bar');
          const nav = document.querySelector('.nav');

          if (currentScrollY > lastScrollY && currentScrollY > 80) {
            announcement?.classList.add('hidden');
            nav?.classList.add('announcement-hidden');
          } else {
            announcement?.classList.remove('hidden');
            nav?.classList.remove('announcement-hidden');
          }

          if (currentScrollY > 10) {
            nav?.classList.add('scrolled');
          } else {
            nav?.classList.remove('scrolled');
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      <div
        className="announcement-bar"
        role="button"
        tabIndex={0}
        onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
        onKeyDown={(e) => e.key === 'Enter' && document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <span>Now with Oracle HCM 25D release coverage</span>
        <span className="announcement-arrow">→</span>
      </div>

      <nav className="nav">
        <div className="nav-left">
          <Link to="/" className="nav-logo">
            <img
              src="/edge-ai-thinking-logo.png"
              alt="Edge AI"
              className="nav-logo-img"
            />
          </Link>
          <div className="nav-links">
            <a href="#features" className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="#security" className="nav-link">Security</a>
          </div>
        </div>
        <div className="nav-right">
          <Link to="/login" className="nav-signin">Sign in</Link>
          <Link to="/register" className="nav-cta">Get started</Link>
        </div>
      </nav>

      <section className="hero">
        <div className="hero-inner">
          <div className="hero-left">
            <div className="hero-badge">
              Oracle HCM Cloud · 25C &amp; 25D
            </div>

            <h1 className="hero-title">
              Your Oracle HCM
              <br />
              expert, available
              <br />
              <span className="hero-title-accent">24/7.</span>
            </h1>

            <p className="hero-subtitle">
              Grounded in Oracle documentation and your tenant config —
              with sources you can cite.
            </p>

            <div className="hero-ctas">
              <Link to="/register" className="cta-primary">
                Get started free →
              </Link>
              <a href="#how-it-works" className="cta-secondary">
                See how it works
              </a>
            </div>
          </div>

          <div className="hero-right">
            <HeroCitationConsole />
          </div>
        </div>
      </section>

      <div className="trust-bar">
        <div className="section-container">
          <p className="trust-label">
            Built for Oracle HCM implementation teams
          </p>
          <div className="trust-items">
            <div className="trust-item">
              <span className="trust-num">25C, 25D + 26A</span>
              <span className="trust-desc">Release coverage</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <span className="trust-num">&lt; 3 sec</span>
              <span className="trust-desc">Response time</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <span className="trust-num">100%</span>
              <span className="trust-desc">Oracle-specific</span>
            </div>
            <div className="trust-divider" />
            <div className="trust-item">
              <span className="trust-num">Zero retention</span>
              <span className="trust-desc">Your data isolated</span>
            </div>
          </div>
        </div>
      </div>

      <section className="features" id="features">
        <div className="section-container">
          <div className="features-layout">
            <div className="features-left">
              <h2 className="section-title">
                Built for Oracle HCM.
                <br />
                Not adapted for it.
              </h2>
              <p className="section-subtitle features-subtitle">
                Generic AI tools give generic answers. Edge AI knows
                Oracle HCM specifically — the modules, the navigation
                paths, the quarterly releases.
              </p>
              <Link to="/register" className="cta-primary features-cta">
                Try it free →
              </Link>
            </div>
            <div className="features-right">
              {features.map((f) => (
                <div key={f.title} className="feature-row">
                  <div className="feature-row-content">
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="how-it-works" id="how-it-works">
        <div className="section-container">
          <div className="hiw-layout">
            <div className="hiw-left">
              <h2 className="section-title">
                As simple as asking a question
              </h2>
              <div className="hiw-steps">
                <div className="hiw-step">
                  <div className="hiw-step-num">1</div>
                  <div className="hiw-step-content">
                    <h3>Ask in plain English</h3>
                    <p>
                      No need to know Oracle documentation structure.
                      Just ask what you need.
                    </p>
                  </div>
                </div>
                <div className="hiw-step">
                  <div className="hiw-step-num">2</div>
                  <div className="hiw-step-content">
                    <h3>Edge AI searches the docs</h3>
                    <p>
                      Searches Oracle HCM documentation and your
                      company&apos;s specific guides simultaneously.
                    </p>
                  </div>
                </div>
                <div className="hiw-step">
                  <div className="hiw-step-num">3</div>
                  <div className="hiw-step-content">
                    <h3>Get a precise answer</h3>
                    <p>
                      Detailed guidance with exact navigation paths,
                      release notes, and next steps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="hiw-right">
              <div className="demo-window">
                <DemoWindowBar url="Edge AI" />
                <AnimatedChatDemo />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="security" id="security">
        <div className="section-container">
          <div className="security-layout">
            <div className="security-left">
              <h2 className="section-title">
                Enterprise-grade security,
                <br />
                built in from day one.
              </h2>
              <p className="section-subtitle security-subtitle">
                Your Oracle HCM data is sensitive. We treat it that way.
                Every architectural decision was made with security first.
              </p>
              <svg
                className="security-isolation-diagram"
                viewBox="0 0 260 140"
                aria-label="Two isolated tenant databases with no shared access"
              >
                {/* Tenant A box */}
                <rect x="8" y="8" width="108" height="90" rx="6" fill="rgba(37,99,235,0.04)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                <text x="62" y="26" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="rgba(248,250,252,0.55)" fontWeight="600" letterSpacing="0.06em">TENANT A</text>
                <rect x="20" y="34" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="20" y="47" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="20" y="60" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="20" y="73" width="60" height="7" rx="2" fill="rgba(255,255,255,0.03)" />
                {/* Divider */}
                <line x1="130" y1="4" x2="130" y2="102" stroke="rgba(255,255,255,0.08)" strokeWidth="1" strokeDasharray="4 3" />
                {/* Tenant B box */}
                <rect x="144" y="8" width="108" height="90" rx="6" fill="rgba(37,99,235,0.04)" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
                <text x="198" y="26" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="rgba(248,250,252,0.55)" fontWeight="600" letterSpacing="0.06em">TENANT B</text>
                <rect x="156" y="34" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="156" y="47" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="156" y="60" width="84" height="7" rx="2" fill="rgba(255,255,255,0.05)" />
                <rect x="156" y="73" width="60" height="7" rx="2" fill="rgba(255,255,255,0.03)" />
                {/* Caption */}
                <text x="130" y="124" textAnchor="middle" fontSize="9" fontFamily="monospace" fill="rgba(255,255,255,0.35)" letterSpacing="0.04em">No shared access. Ever.</text>
              </svg>
            </div>
            <div className="security-right">
              {securityItems.map((item) => (
                <div key={item.title} className="security-item">
                  <div className="security-check">✓</div>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="section-container">
          <div className="cta-box">
            <h2 className="cta-title">
              Stop waiting for answers
            </h2>
            <p className="cta-desc">
              Your Oracle HCM team deserves better than
              searching through documentation for hours. Edge AI gives
              them instant, accurate answers.
            </p>
            <div className="cta-buttons">
              <Link to="/register" className="cta-primary">
                Create free account →
              </Link>
              <Link to="/login" className="cta-secondary">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="section-container">
          <div className="footer-top">
            <div className="footer-brand-full">
              <img
                src="/edge-ai-thinking-logo.png"
                alt="Edge AI"
                className="footer-logo-img"
              />
              <p className="footer-tagline">
                Your Oracle HCM expert, available 24/7.
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <h4>Product</h4>
                <Link to="/register">Get started</Link>
                <Link to="/login">Sign in</Link>
              </div>
              <div className="footer-col">
                <h4>Legal</h4>
                <Link to="/terms">Terms</Link>
                <Link to="/privacy">Privacy</Link>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2025 StratusEdge Solutions Inc. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
